<template>
  <!-- 受注の「作業曜日・時間」の新しい形（曜日ごと）の入力欄。契約詳細入力画面の行と同じ並び（勤務形態・開始〜終了・実働・コピー貼付） -->
  <div class="remark-days">
    <!-- 行ごとのコピー・貼付（7曜日ぶんまるごと。別の行（別の人）に貼る。契約詳細入力画面と同じで確認ダイアログは無し） -->
    <div v-if="!disabled" class="row-copy">
      <button type="button" class="day-btn copy" @click="copyRow">週をコピー</button>
      <button type="button" class="day-btn paste" :disabled="!clipboard.row" @click="pasteRow">週に貼付</button>
    </div>
    <div class="flex">
      <div v-for="w in weeks" :key="w" class="day-col" :class="isOn(w) ? 'day-on' : ''">
        <!-- 曜日（押すと勤務あり／なし） -->
        <div
          class="day-head"
          :class="[isOn(w) ? 'on' : '', disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
          v-tooltip="disabled ? '' : (isOn(w) ? 'クリックするとこの曜日を勤務なしにします' : 'クリックするとこの曜日を勤務あり（毎週）にします')"
          @click="toggleDay(w)"
        >
          {{ w }}
        </div>

        <!-- 入力欄 -->
        <div v-if="!disabled" class="day-body">
          <multiselect
            v-model="remark.days[w].weekDetail"
            :options="weekDetailList"
            :multiple="true"
            :close-on-select="false"
            :show-labels="false"
            :searchable="false"
            :allow-empty="true"
            :hide-selected="true"
            :max-height="160"
            placeholder="-"
            class="multiselect"
            @input="detailChanged(w)"
          />
          <template v-if="isOn(w)">
            <div class="times">
              <AkbsTimeInput v-model="remark.days[w].startTime" @change="setTime(w)" />
              <span class="tilde">~</span>
              <AkbsTimeInput v-model="remark.days[w].endTime" @change="setTime(w)" />
            </div>
            <div class="work">
              実働
              <input
                type="number"
                v-model="remark.days[w].hours"
                step="0.25"
                min="0"
                class="wh"
                @wheel="$event.target.blur()"
                @focus="inputFocus"
                @change="$emit('change')"
              />
              h
            </div>
          </template>
          <!-- コピー貼付（契約詳細入力画面と同じ。別の行（別の人）にも貼れる） -->
          <div class="day-copy">
            <button type="button" class="day-btn copy" @click="copyDay(w)">コピー</button>
            <button type="button" class="day-btn paste" :disabled="!clipboard.day" @click="pasteDay(w)">貼付</button>
          </div>
        </div>

        <!-- 表示だけ（サブタスク画面・参照のとき） -->
        <div v-else class="day-body view">
          <template v-if="isOn(w)">
            <div class="attendance-labels">
              <div v-for="(d, k) in remark.days[w].weekDetail" :key="'v' + k" class="attendance-label">{{ d }}</div>
            </div>
            <div class="times view-text">{{ remark.days[w].startTime || '-' }}<span class="tilde">~</span>{{ remark.days[w].endTime || '-' }}</div>
            <div class="work">実働 <span class="font-bold">{{ remark.days[w].hours || '0' }}</span> h</div>
          </template>
          <div v-else class="off-text">-</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Multiselect from 'vue-multiselect'
import AkbsTimeInput from '../../Akbs/components/AkbsTimeInput.vue'
import * as constants from '@libs/constants'
import * as utils from '@libs/utils'
import { WEEKS, isDayOn, syncWorkDays, blankDay, hoursFromTimes } from '@libs/orderRemark'

// コピーした曜日の勤務（day）と行まるごとの勤務（row）。画面の中で1つずつ。別の行（別の人）にも貼れるように部品の外で持つ
const clipboard = Vue.observable({ day: null, row: null })

export default {
  components: {
    Multiselect,
    AkbsTimeInput
  },

  props: {
    // 新しい形の1行（{ days: { 月: { weekDetail, startTime, endTime, hours } … }, workDays }）。この中を直接書き換える
    remark: {
      type: Object,
      required: true
    },
    // 表示だけ（サブタスク画面・参照）
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      weeks: WEEKS,
      weekDetailList: constants.WEEK_DETAIL,
      clipboard
    }
  },

  methods: {
    isOn(w) {
      return isDayOn(this.remark, w)
    },

    /**
     * 曜日を押したとき：勤務ありなら無しに（時間も消す）、無しなら毎週で勤務ありに
     */
    toggleDay(w) {
      if (this.disabled) {
        return
      }
      if (this.isOn(w)) {
        this.remark.days[w] = blankDay()
      } else {
        this.remark.days[w] = { ...blankDay(), weekDetail: ['毎週'] }
      }
      this.changed()
    },

    /**
     * 勤務形態を変えたとき（全部外したら勤務なしにして時間も消す）
     */
    detailChanged(w) {
      if (!this.isOn(w)) {
        this.remark.days[w] = blankDay()
      }
      this.changed()
    },

    /**
     * 開始・終了を入れたら実働を出す（契約詳細入力画面と同じ。6時間を超えれば休憩1時間を引く。画面で直せる）
     */
    async setTime(w) {
      await this.$nextTick()
      const day = this.remark.days[w]
      day.hours = hoursFromTimes(day.startTime, day.endTime)
      this.changed()
    },

    copyDay(w) {
      clipboard.day = utils.clone(this.remark.days[w])
    },

    pasteDay(w) {
      if (!clipboard.day) {
        return
      }
      this.remark.days[w] = utils.clone(clipboard.day)
      this.changed()
    },

    /**
     * 行まるごと（7曜日の勤務）のコピー・貼付。貼付はこの行の全曜日を上書き
     */
    copyRow() {
      clipboard.row = utils.clone(this.remark.days)
    },

    pasteRow() {
      if (!clipboard.row) {
        return
      }
      for (const w of WEEKS) {
        this.remark.days[w] = clipboard.row[w] ? utils.clone(clipboard.row[w]) : blankDay()
      }
      this.changed()
    },

    inputFocus(e) {
      utils.inputFocus(e)
    },

    changed() {
      syncWorkDays(this.remark)
      this.$emit('change')
    }
  }
}
</script>

<style scoped>
.remark-days {
  margin-top: 4px;
}

.day-col {
  width: 14%;
  margin-right: 1px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.day-col.day-on {
  background: #f5f7ff;
}

.day-head {
  padding: 6px 0;
  text-align: center;
  font-size: 14px;
  background: #fff;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.day-head.on {
  background: #312e81;
  color: #fff;
  font-weight: 700;
}

.day-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 4px;
  flex: 1;
}

.attendance-labels {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.attendance-label {
  margin: 0 auto;
  padding: 2px 0;
  width: 80%;
  border-radius: 4px;
  background: #15803d;
  color: #fff;
  font-weight: 700;
  text-align: center;
  font-size: 11px;
}

.times {
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: center;
}

.times.view-text {
  font-size: 11px;
}

.times >>> input {
  flex: 1;
  min-width: 0;
}

.tilde {
  flex: none;
  color: #6b7280;
  font-size: 11px;
  padding: 0 2px;
}

.work {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 11px;
  color: #4b5563;
}

.work input.wh {
  width: 48px;
  height: 26px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  padding: 0 2px;
}

.off-text {
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
  padding: 6px 0;
}

/* 行ごとのコピー・貼付（表の右上） */
.row-copy {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-bottom: 4px;
}

.row-copy .day-btn {
  flex: none;
  padding: 2px 10px;
  font-size: 11px;
}

/* コピー貼付（契約詳細入力画面と同じ見た目） */
.day-copy {
  display: flex;
  gap: 4px;
  margin-top: auto;
  padding-top: 2px;
}

.day-btn {
  flex: 1;
  padding: 2px 0;
  border: 1px solid;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.day-btn.copy {
  border-color: #4338ca;
  background: #eef2ff;
  color: #4338ca;
}

.day-btn.copy:hover:not(:disabled) {
  background: #c7d2fe;
}

.day-btn.paste {
  border-color: #3730a3;
  background: #c7d2fe;
  color: #312e81;
}

.day-btn.paste:hover:not(:disabled) {
  background: #a5b4fc;
}

.day-btn:disabled {
  border-color: #d1d5db;
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

/* 勤務形態の複数選択（契約詳細入力画面と同じ） */
.multiselect >>> .multiselect__tags {
  padding-right: 6px !important;
  min-height: 28px;
  padding-top: 4px;
  font-size: 12px;
  text-align: center;
}

.multiselect >>> .multiselect__select {
  display: none;
}
</style>
